import { invalidateCommunitySettingsGuard } from '@/apis/queries/community-settings';
import { performanceAlertsAccessGuard } from '@/app/settings/utils/performance-alerts';
import { publishingAutopilotAccessGuard } from '@/app/settings/utils/publishing-autopilot';
import { BRAND, ORGANIZATION_USER, USER } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';

const App = () => import('@/app/settings/App.vue');
const ShareableDashboardLinks = () => import('@/app/settings/pages/ShareableDashboardLinks.vue');
const Profile = () => import('@/app/settings/pages/Profile.vue');
const AuthorizedApps = () => import('@/app/settings/pages/AuthorizedApps.vue');
const ScheduledEmails = () => import('@/app/settings/pages/ScheduledEmails.vue');
const SocialPlatform = () => import('@/app/settings/pages/PlatformConnectionsWrapper.vue');
const TeamAndPermissions = () => import('@/app/settings/pages/TeamAndPermissions.vue');
const EmailNotificationPreferences = () =>
  import('@/app/settings/pages/EmailNotificationPreferences.vue');
const CompetitorManagement = () => import('@/app/settings/pages/CompetitorManagement.vue');
const ContentTags = () => import('@/app/settings/pages/ContentTags.vue');
const ContentAutomation = () => import('@/app/settings/pages/ContentAutomationWrapper.vue');
const PerformanceAlerts = () => import('@/app/settings/pages/PerformanceAlerts.vue');
const PublishingAutopilot = () => import('@/app/settings/pages/PublishingAutopilot.vue');
const BrandManagement = () => import('@/app/settings/pages/BrandManagement.vue');
const Developer = () => import('@/app/settings/pages/Developers.vue');
const Integrations = () => import('@/app/settings/pages/Integrations.vue');
const Billing = () => import('@/app/settings/pages/Billing.vue');
const UtmManagement = () => import('@/app/settings/pages/UtmManagement.vue');
const CreatorEmailConnection = () => import('@/app/settings/pages/CreatorEmailConnection.vue');
const CreatorProfileFields = () => import('@/app/settings/pages/CreatorProfileFields.vue');
const CustomMetrics = () => import('@/app/settings/pages/CustomMetrics.vue');
const CreatorPayorProfile = () => import('@/app/settings/pages/CreatorPayorProfile.vue');
const CommunityAgent = () => import('@/app/settings/pages/CommunityAgent.vue');
const Community = () => import('@/app/settings/pages/Community.vue');
const VisionAgentsMoved = () => import('@/app/settings/pages/VisionAgentsMoved.vue');

const VISION_AGENT_HUB_ROUTES = {
  'settings.contentAutomation': 'vision.tasks.live.content',
  'settings.communityAgent': 'vision.tasks.live.community',
  'settings.performanceAlerts': 'vision.tasks.live.performance',
};

async function redirectVisionAgentToHub(to) {
  const flagStore = useFlagStore();
  await flagStore.waitForUserFlags;
  if (!flagStore.ready || !flagStore.flags.visionHub) return true;
  if (to.name === 'settings.contentAutomation' && !flagStore.flags.visionAgentAutomations) {
    return true;
  }

  return {
    name: VISION_AGENT_HUB_ROUTES[to.name],
    params: { brandLabel: to.params.brandLabel },
    query: to.query,
    hash: to.hash,
  };
}

async function showVisionAgentsMoved(to) {
  const flagStore = useFlagStore();
  await flagStore.waitForUserFlags;
  if (flagStore.ready && flagStore.flags.visionHub) return true;

  return { name: 'settings.profile', params: { brandLabel: to.params.brandLabel } };
}

const PAGE_LEVEL_TOP = `Settings`;

export default {
  path: 'settings',
  component: App,
  meta: {
    requiresAuth: true,
    allowsAds: true,
    allowWhenNoConnections: true,
  },
  children: [
    {
      name: 'settings',
      path: '',
      redirect: { name: 'settings.profile' },
    },
    {
      name: 'settings.profile',
      path: 'profile',
      component: Profile,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Profile`,
      },
    },
    {
      name: 'settings.scheduledEmails',
      path: 'scheduled-emails',
      component: ScheduledEmails,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Scheduled Emails`,
      },
    },
    {
      name: 'settings.authorizedApps',
      path: 'authorized-apps',
      component: AuthorizedApps,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Authorized Apps`,
      },
    },
    {
      name: 'settings.socialPlatform',
      path: 'social_platform',
      component: SocialPlatform,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Social Platform Connections`,
      },
    },
    {
      name: 'settings.team.user',
      path: 'team/:id',
      redirect: { name: 'settings.notifications.email' },
    },
    {
      name: 'settings.permissions',
      path: 'permissions',
      component: TeamAndPermissions,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Team and Permissions`,
      },
    },
    {
      name: 'settings.creatorProfileFields',
      path: 'creator-profile-fields',
      component: CreatorProfileFields,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Creator Profile Fields`,
      },
    },
    {
      name: 'settings.notifications.email',
      path: 'notifications/email',
      component: EmailNotificationPreferences,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Email Preferences`,
      },
    },
    {
      name: 'settings.competitors',
      path: 'competitors',
      component: CompetitorManagement,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Competitors`,
      },
    },
    {
      name: 'settings.contentTags',
      path: 'content-tags',
      component: ContentTags,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Content Tags`,
      },
    },
    {
      name: 'settings.visionAgentsMoved',
      path: 'vision-agents',
      component: VisionAgentsMoved,
      meta: { analytics: `${PAGE_LEVEL_TOP} - Vision Agents` },
      beforeEnter: showVisionAgentsMoved,
    },
    {
      name: 'settings.contentAutomation',
      path: 'content-automation',
      component: ContentAutomation,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Content Automation`,
      },
      beforeEnter: redirectVisionAgentToHub,
    },
    {
      name: 'settings.performanceAlerts',
      path: 'performance-alerts',
      component: PerformanceAlerts,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Performance Alerts`,
      },
      beforeEnter: [performanceAlertsAccessGuard, redirectVisionAgentToHub],
    },
    {
      name: 'settings.communityAgent',
      path: 'community-agent',
      component: CommunityAgent,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Community Agent`,
      },
      beforeEnter: redirectVisionAgentToHub,
    },
    {
      name: 'settings.publishingAutopilot',
      path: 'publishing-autopilot',
      component: PublishingAutopilot,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Publishing Autopilot`,
      },
      beforeEnter: publishingAutopilotAccessGuard,
    },
    {
      name: 'settings.community',
      path: 'community',
      component: Community,
      beforeEnter: invalidateCommunitySettingsGuard,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Community`,
      },
    },
    {
      name: 'settings.utm',
      path: 'utm-management',
      component: UtmManagement,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - UTM Management`,
      },
    },
    {
      name: 'settings.creatorEmail',
      path: 'creator-email-connections',
      component: CreatorEmailConnection,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Creator Email Connections`,
      },
    },
    {
      name: 'settings.brands',
      path: 'brands',
      component: BrandManagement,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Brands`,
      },
    },
    {
      name: 'settings.developer',
      path: 'developer_page',
      component: Developer,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Developer`,
      },
    },
    {
      name: 'settings.creatorPayments',
      path: 'creator-payments',
      component: CreatorPayorProfile,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Creator Payor Profile`,
      },
    },
    {
      name: 'settings.integrations',
      path: 'integrations',
      component: Integrations,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Integrations`,
      },
    },
    {
      name: 'settings.billing',
      path: 'billing',
      component: Billing,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Billing & Plan`,
      },
    },
    {
      name: 'settings.socialListeningUpgrade',
      path: 'social-listening-upgrade',
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Social Listening Upgrade`,
        modal: {
          component: 'SocialListeningUpgradeModal',
        },
      },
    },
    {
      name: 'settings.customMetrics',
      path: 'custom-metrics',
      component: CustomMetrics,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Custom Metrics`,
      },
      beforeEnter: (to, from, next) => {
        const authStore = useAuthStore();
        const identityStore = useIdentityStore();
        const canAccess =
          authStore.checkIfBrandPermissionsExist(
            [BRAND.SETTINGS.CAN_ACCESS_CUSTOM_METRICS],
            [authStore.currentBrand],
          ) || identityStore.guard(ORGANIZATION_USER.SETTINGS.CAN_MANAGE_CUSTOM_METRICS);
        if (!canAccess) {
          next({
            name: 'settings.profile',
            params: {
              brandLabel: identityStore.currentBrand?.label,
            },
          });
        } else {
          next();
        }
      },
    },
    {
      name: 'settings.shareableDashboardLinks',
      path: 'shareable-dashboard-links',
      component: ShareableDashboardLinks,
      meta: {
        analytics: `${PAGE_LEVEL_TOP} - Shareable Dashboard Links`,
      },
      beforeEnter: (to, from, next) => {
        const authStore = useAuthStore();
        const identityStore = useIdentityStore();
        const canAccess =
          authStore.checkIfBrandPermissionsExist(
            [BRAND.DASHBOARD.CAN_CREATE_SHAREABLE_DASHBOARD_LINKS],
            [authStore.currentBrand],
          ) &&
          authStore.checkIfUserPermissionsExist([
            USER.DASHBOARD.CAN_CREATE_SHAREABLE_DASHBOARD_LINKS,
          ]);
        if (authStore.isCurrentBrandRoleAdmin || canAccess) {
          next();
        } else {
          next({
            name: 'settings.profile',
            params: {
              brandLabel: identityStore.currentBrand?.label,
            },
          });
        }
      },
    },
  ],
};
